// Quick status tabs beside the filter button: one-click filtering on the
// main job statuses (enclosed Chakra tabs; legacy-ui's tabs recipe pins the
// brand palette on the list, so the selected trigger carries the theme's
// brand accent). They drive the SAME URL status filter the filter popover
// manages — a tab selection shows up in the popover's Status row, and a
// popover selection of exactly one of these statuses lights its tab back.
// 'All' (and deselecting the selected tab — zag `deselectable`) clears the
// status filter; a filter the tabs can't express (canceled, ready,
// submitted, multi-select) lights nothing.

import { Tabs } from '@chakra-ui/react';
import type { UiFilters } from '../../lib/jobs/filters';

const TAB_STATUSES = ['completed', 'failed', 'running'] as const;

export function StatusTabs({
    filters,
    setFilters,
}: {
    filters: UiFilters;
    setFilters: (newFilters: UiFilters) => void;
}) {
    const selected = filters.status ?? [];
    const single = selected.length === 1 ? selected[0]?.value : undefined;
    const value =
        selected.length === 0
            ? 'all'
            : single && (TAB_STATUSES as readonly string[]).includes(single)
              ? single
              : null;

    return (
        <Tabs.Root
            variant='enclosed'
            size='sm'
            mr='8px'
            // Click-again deselects: zag emits value: null and the handler
            // clears the status filter, so the bar resolves back to 'All'.
            deselectable
            // Select on click/Enter only — the default 'automatic' mode
            // selects on focus, so arrow-keying through the bar would fire
            // a server refetch per keypress (each pick swaps the listing).
            activationMode='manual'
            // The toolbar's sm icon buttons are 36px; the enclosed list pads
            // 4px around the triggers, so 28px triggers put the whole bar on
            // the same 36px line (stock sm triggers would make it 44px).
            css={{
                '& [data-part="trigger"]': {
                    height: '28px',
                },
            }}
            value={value}
            onValueChange={({ value: next }) =>
                setFilters({
                    ...filters,
                    status:
                        !next || next === 'all'
                            ? []
                            : [{ value: next, label: next }],
                })
            }
        >
            <Tabs.List>
                <Tabs.Trigger value='all'>All</Tabs.Trigger>
                <Tabs.Trigger value='running'>Running</Tabs.Trigger>
                <Tabs.Trigger value='completed'>Completed</Tabs.Trigger>
                <Tabs.Trigger value='failed'>Failed</Tabs.Trigger>
            </Tabs.List>
        </Tabs.Root>
    );
}
